<template>
  <!-- Permission Indicator Icon - visible only for admins -->
  <!-- Wrapped in span to fully isolate click events from parent menu items -->
  <span 
    v-if="visible" 
    class="permission-indicator-wrapper"
    @click.stop.prevent="handleClick"
    @mousedown.stop.prevent
    @mouseup.stop.prevent
  >
    <span class="permission-indicator" @click.stop.prevent="handleClick">
      <v-icon size="14" color="grey-darken-2">mdi-shield-key-outline</v-icon>
    </span>
    <v-tooltip activator="parent" location="top" :open-delay="300">
      Click to manage permissions
    </v-tooltip>
  </span>
  
  <!-- Roles Dialog - persistent to prevent accidental closing -->
  <v-dialog v-model="dialog" max-width="600" :scrim="true" persistent>
    <v-card>
      <v-card-title class="d-flex align-center pa-3 bg-primary">
        <v-icon class="mr-2" color="white">mdi-shield-account</v-icon>
        <span class="text-white text-body-1">Permission Management</span>
        <v-spacer />
        <v-btn icon size="small" variant="text" @click="closeDialog">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-card-text class="pa-3">
        <!-- Loading state -->
        <div v-if="loading" class="text-center py-4">
          <v-progress-circular indeterminate size="32" color="primary" />
          <div class="text-body-2 mt-2 text-grey">Loading permissions...</div>
        </div>
        
        <!-- Error state -->
        <v-alert v-else-if="error" type="error" density="compact" class="mb-3" closable @click:close="error = null">
          {{ error }}
        </v-alert>
        
        <!-- Content -->
        <template v-if="!loading">
          <!-- Module Info -->
          <div class="mb-3 pa-2 bg-grey-lighten-4 rounded">
            <div class="d-flex align-center">
              <div>
                <div class="text-overline text-grey mb-0">Module</div>
                <div class="text-h6 font-weight-medium">{{ modulePrefix }}</div>
              </div>
              <v-spacer />
              <v-chip size="small" color="info" variant="tonal">
                {{ relatedPermissions.length }} permission{{ relatedPermissions.length !== 1 ? 's' : '' }}
              </v-chip>
            </div>
          </div>
          
          <!-- All Related Permissions grouped -->
          <div class="mb-3">
            <div class="d-flex align-center mb-2">
              <div class="text-overline text-grey">
                Module Permissions
              </div>
              <v-spacer />
              <!-- Add Role Button -->
              <v-btn
                v-if="availableRoles.length > 0 && selectedPermission"
                size="small"
                color="success"
                variant="tonal"
                prepend-icon="mdi-plus"
                @click="showAddRole = !showAddRole"
              >
                Add Role
              </v-btn>
            </div>
            
            <!-- Add Role Section -->
            <v-expand-transition>
              <div v-if="showAddRole" class="mb-3 pa-3 bg-green-lighten-5 rounded border">
                <div class="text-body-2 font-weight-medium mb-2">
                  Add "{{ selectedPermission?.code }}" to role:
                </div>
                <v-autocomplete
                  v-model="selectedRoleToAdd"
                  :items="availableRolesForSelected"
                  item-title="displayName"
                  item-value="id"
                  item-props="props"
                  label="Select role"
                  density="compact"
                  variant="outlined"
                  hide-details
                  class="mb-2"
                  :loading="loadingRoles"
                  clearable
                >
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props">
                      <template v-slot:prepend>
                        <v-chip 
                          size="x-small" 
                          :color="item.raw.domain_name === 'Global' ? 'primary' : 'secondary'"
                          variant="flat"
                          class="mr-2"
                        >
                          {{ item.raw.domain_name }}
                        </v-chip>
                      </template>
                    </v-list-item>
                  </template>
                </v-autocomplete>
                <div class="d-flex justify-end gap-2">
                  <v-btn size="small" variant="text" @click="showAddRole = false; selectedRoleToAdd = null">
                    Cancel
                  </v-btn>
                  <v-btn 
                    size="small" 
                    color="success" 
                    variant="flat"
                    :disabled="!selectedRoleToAdd"
                    :loading="saving"
                    @click="addPermissionToRole"
                  >
                    Add Permission
                  </v-btn>
                </div>
              </div>
            </v-expand-transition>
            
            <!-- Permissions Accordion -->
            <v-expansion-panels v-model="expandedPanels" multiple variant="accordion">
              <v-expansion-panel
                v-for="perm in displayedPermissions"
                :key="perm.id"
                :value="perm.code"
                class="permission-panel"
                :class="{ 'highlighted': perm.is_requested }"
              >
                <v-expansion-panel-title class="py-2">
                  <div class="d-flex align-center flex-grow-1">
                    <v-chip 
                      size="x-small" 
                      :color="getActionColor(perm.action)"
                      variant="flat"
                      class="mr-2"
                    >
                      {{ perm.action }}
                    </v-chip>
                    <span class="text-body-2 font-weight-medium">{{ perm.code }}</span>
                    <v-spacer />
                    <v-chip 
                      size="x-small" 
                      :color="perm.roles_count > 0 ? 'success' : 'grey'"
                      variant="tonal"
                      class="mr-2"
                    >
                      {{ perm.roles_count }} role{{ perm.roles_count !== 1 ? 's' : '' }}
                    </v-chip>
                    <v-icon 
                      v-if="perm.is_requested" 
                      size="16" 
                      color="warning"
                    >
                      mdi-star
                    </v-icon>
                  </div>
                </v-expansion-panel-title>
                
                <v-expansion-panel-text>
                  <div class="text-caption text-grey mb-2" v-if="perm.description">
                    {{ perm.description }}
                  </div>
                  
                  <!-- Roles for this permission -->
                  <v-list v-if="perm.roles.length > 0" density="compact" class="pa-0 roles-list">
                    <v-list-item
                      v-for="role in perm.roles"
                      :key="role.id"
                      class="px-2 rounded mb-1"
                    >
                      <template v-slot:prepend>
                        <v-chip 
                          size="x-small" 
                          :color="role.domain_name === 'Global' ? 'primary' : 'secondary'"
                          variant="flat"
                          class="mr-2"
                        >
                          {{ role.domain_name }}
                        </v-chip>
                      </template>
                      <v-list-item-title class="text-body-2">
                        {{ role.name }}
                        <v-icon 
                          v-if="role.is_system" 
                          size="14" 
                          color="warning" 
                          class="ml-1"
                        >
                          mdi-shield-star
                        </v-icon>
                      </v-list-item-title>
                      <!-- Remove button -->
                      <template v-slot:append>
                        <v-btn
                          icon
                          size="x-small"
                          variant="text"
                          color="error"
                          @click.stop="removePermissionFromRole(perm, role)"
                          :loading="removingKey === `${perm.id}-${role.id}`"
                        >
                          <v-icon size="16">mdi-close</v-icon>
                          <v-tooltip activator="parent" location="left">Remove</v-tooltip>
                        </v-btn>
                      </template>
                    </v-list-item>
                  </v-list>
                  
                  <div v-else class="text-center py-2 text-grey text-caption">
                    No roles assigned
                  </div>
                  
                  <!-- Quick add button for this permission -->
                  <v-btn
                    v-if="availableRoles.length > 0"
                    size="small"
                    color="success"
                    variant="text"
                    prepend-icon="mdi-plus"
                    class="mt-2"
                    @click.stop="selectPermissionAndShowAdd(perm)"
                  >
                    Add role to this permission
                  </v-btn>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- Toggle: show/hide the module's additional (sub) permissions.
                 Keeps the dialog focused on the main permission by default. -->
            <div v-if="subPermissions.length > 0" class="text-center mt-1">
              <v-btn
                size="small"
                variant="text"
                color="primary"
                :prepend-icon="showSubPermissions ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                @click="showSubPermissions = !showSubPermissions"
              >
                {{ showSubPermissions
                  ? 'Hide additional permissions'
                  : `Show ${subPermissions.length} more permission${subPermissions.length !== 1 ? 's' : ''} in this module` }}
              </v-btn>
            </div>
            
            <v-alert 
              v-if="relatedPermissions.length === 0" 
              type="info" 
              density="compact" 
              variant="tonal"
              class="mb-0"
            >
              No permissions found for this module
            </v-alert>
          </div>
          
          <!-- Success message -->
          <v-alert 
            v-if="successMessage" 
            type="success" 
            density="compact" 
            variant="tonal"
            class="mb-0"
            closable
            @click:close="successMessage = ''"
          >
            {{ successMessage }}
          </v-alert>
          
          <!-- Info about cache and refresh button -->
          <v-alert 
            type="warning" 
            density="compact" 
            variant="tonal"
            class="mt-3"
            icon="mdi-alert"
          >
            <div class="d-flex align-center">
              <span class="text-caption flex-grow-1">
                Users must re-login or refresh to see changes. 
                <strong>Refresh your own permissions:</strong>
              </span>
              <v-btn 
                size="x-small" 
                color="warning" 
                variant="flat"
                class="ml-2"
                :loading="refreshingMyPermissions"
                @click="refreshMyPermissions"
              >
                Refresh My Permissions
              </v-btn>
            </div>
          </v-alert>
          
          <v-alert 
            v-if="myPermissionsRefreshed" 
            type="success" 
            density="compact" 
            variant="tonal"
            class="mt-2"
            closable
            @click:close="myPermissionsRefreshed = false"
          >
            Your permissions refreshed! Page will reload...
          </v-alert>
        </template>
      </v-card-text>
      
      <v-divider />
      
      <v-card-actions class="pa-2">
        <v-btn 
          size="small" 
          variant="text" 
          prepend-icon="mdi-refresh"
          @click="loadRoles"
          :loading="loading"
        >
          Refresh List
        </v-btn>
        <v-spacer />
        <v-btn variant="flat" size="small" color="primary" @click="closeDialog">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref } from 'vue'

// Shared flag: true while ANY permission dialog is open.
// App.vue uses it to keep the submenu flyout mounted (the dialog is a child of
// the flyout, so closing the submenu would unmount/destroy the dialog).
export const permissionDialogOpen = ref(false)
</script>

<script setup>
import { computed, watch, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import axios from '@/api/client'
import {
  isPositionTemplateRole,
  POSITION_TEMPLATE_SUBTITLE,
  sortRolesForPicker,
} from '@/utils/positionRoleTemplates'

const props = defineProps({
  // Permission code or array of permission codes
  permissionCode: {
    type: [String, Array],
    required: true
  }
})

const authStore = useAuthStore()
const dialog = ref(false)
const loading = ref(false)
const loadingRoles = ref(false)
const saving = ref(false)
const error = ref(null)
const successMessage = ref('')
const allRoles = ref([])
const modulePrefix = ref('')
const relatedPermissions = ref([])
const expandedPanels = ref([])

// Add role functionality
const showAddRole = ref(false)
// Collapse the module's extra (sub) permissions by default so the dialog stays
// focused on the main menu-access permission (e.g. dashboard.view).
const showSubPermissions = ref(false)
const refreshingMyPermissions = ref(false)
const myPermissionsRefreshed = ref(false)
const selectedRoleToAdd = ref(null)
const selectedPermission = ref(null)
const removingKey = ref(null)

// Check if user can see permission indicators
const visible = computed(() => {
  return authStore.hasPermission('admin.view_permission_indicators')
})

// Get the first permission code for API call
const primaryPermissionCode = computed(() => {
  if (Array.isArray(props.permissionCode)) {
    return props.permissionCode[0]
  }
  return props.permissionCode
})

// The "main" permission is the one the shield was opened with (starred /
// is_requested) — it maps to actually opening the menu item. Fallback to the
// first permission if the backend didn't flag one.
const mainPermissions = computed(() => {
  const reqs = relatedPermissions.value.filter(p => p.is_requested)
  if (reqs.length) return reqs
  return relatedPermissions.value.slice(0, 1)
})

// The parent permission all sub-permissions inherit access from.
const parentPermission = computed(() => mainPermissions.value[0] || null)

// Sub-permissions = the rest of the module (view_tasks, etc.).
const subPermissions = computed(() => {
  const mainCodes = new Set(mainPermissions.value.map(p => p.code))
  return relatedPermissions.value.filter(p => !mainCodes.has(p.code))
})

// What the accordion actually renders: main only, or main + subs when expanded.
const displayedPermissions = computed(() =>
  showSubPermissions.value
    ? [...mainPermissions.value, ...subPermissions.value]
    : [...mainPermissions.value]
)

// Get action color based on action type
const getActionColor = (action) => {
  const colors = {
    'view': 'info',
    'read': 'info',
    'create': 'success',
    'add': 'success',
    'edit': 'warning',
    'update': 'warning',
    'manage': 'warning',
    'delete': 'error',
    'remove': 'error',
    'admin': 'purple',
    'administration': 'purple'
  }
  return colors[action?.toLowerCase()] || 'grey'
}

// Available roles (not yet assigned to selected permission)
const availableRolesForSelected = computed(() => {
  if (!selectedPermission.value) return []
  const assignedIds = new Set(selectedPermission.value.roles.map(r => r.id))
  return sortRolesForPicker(
    allRoles.value
      .filter(r => !assignedIds.has(r.id) && r.is_active)
      .map(r => ({
        ...r,
        displayName: isPositionTemplateRole(r)
          ? `${r.name} (${r.domain_name || 'Global'})`
          : `${r.name} (${r.domain_name || 'Global'})`,
        props: isPositionTemplateRole(r)
          ? { subtitle: POSITION_TEMPLATE_SUBTITLE }
          : undefined,
      })),
  )
})

// All available roles
const availableRoles = computed(() => {
  return allRoles.value.filter(r => r.is_active)
})

// Handle click with full event isolation
const handleClick = (event) => {
  // Completely stop event propagation
  if (event) {
    event.stopPropagation()
    event.preventDefault()
    event.stopImmediatePropagation()
  }
  console.log('[PermissionIndicator] Click intercepted, opening dialog for:', props.permissionCode)
  openDialog()
  return false
}

// Close dialog
const closeDialog = () => {
  dialog.value = false
  showAddRole.value = false
  selectedRoleToAdd.value = null
  selectedPermission.value = null
  successMessage.value = ''
}

// Open dialog and load roles
const openDialog = async () => {
  console.log('[PermissionIndicator] Opening dialog for permission:', props.permissionCode)
  dialog.value = true
  error.value = null
  successMessage.value = ''
  // loadRoles now returns the domain-scoped assignable roles list from the
  // backend (current company + global only). Only fall back to the legacy
  // /admin/roles/ endpoint if the backend did not provide it.
  await loadRoles()
  if (!allRoles.value.length) {
    await loadAllRoles()
  }
  
  // Start with sub-permissions collapsed; focus on the main permission.
  showSubPermissions.value = false

  // Auto-expand the requested permission
  if (primaryPermissionCode.value) {
    expandedPanels.value = [primaryPermissionCode.value]
  }
}

// Load all roles in system
const loadAllRoles = async () => {
  loadingRoles.value = true
  try {
    const response = await axios.get('/admin/roles/')
    allRoles.value = sortRolesForPicker(
      (response.data.results || response.data || []).map(r => ({
        ...r,
        domain_name: r.domain?.name || r.domain_name || 'Global',
      })),
    )
    console.log('[PermissionIndicator] Loaded all roles:', allRoles.value.length)
  } catch (err) {
    console.error('[PermissionIndicator] Error loading all roles:', err)
  } finally {
    loadingRoles.value = false
  }
}

// Load permissions and roles
const loadRoles = async () => {
  if (!primaryPermissionCode.value) {
    error.value = 'No permission code provided'
    return
  }
  
  loading.value = true
  error.value = null
  relatedPermissions.value = []
  
  try {
    // Encode permission code for URL (handle dots in code like dashboard.view)
    const encodedCode = encodeURIComponent(primaryPermissionCode.value)
    const response = await axios.get(`/admin/permissions/by-code/${encodedCode}/roles/`)
    
    console.log('[PermissionIndicator] API response:', response.data)
    
    modulePrefix.value = response.data.module_prefix || ''
    relatedPermissions.value = response.data.related_permissions || []

    // Domain-scoped assignable roles for the "Add role" dropdown (current
    // company + global only). Prevents other tenants' roles from appearing
    // even for platform/superadmin users.
    if (Array.isArray(response.data.assignable_roles)) {
      allRoles.value = sortRolesForPicker(
        response.data.assignable_roles.map(r => ({
          ...r,
          domain_name: r.domain?.name || r.domain_name || 'Global',
        })),
      )
    }
    
    // If no related permissions returned, create one from the main response
    if (relatedPermissions.value.length === 0) {
      relatedPermissions.value = [{
        id: response.data.permission_id,
        code: response.data.permission_code,
        action: response.data.permission_action,
        description: response.data.permission_description,
        roles: response.data.roles || [],
        roles_count: response.data.roles_count || 0,
        is_requested: true
      }]
    }
    
  } catch (err) {
    console.error('[PermissionIndicator] Error loading roles:', err)
    error.value = err.response?.data?.error || 'Failed to load permissions'
  } finally {
    loading.value = false
  }
}

// Select permission and show add role form
const selectPermissionAndShowAdd = (perm) => {
  selectedPermission.value = perm
  showAddRole.value = true
  selectedRoleToAdd.value = null
}

// Add permission to selected role
const addPermissionToRole = async () => {
  if (!selectedRoleToAdd.value || !selectedPermission.value) {
    error.value = 'Please select a role'
    return
  }
  
  saving.value = true
  error.value = null
  successMessage.value = ''
  
  try {
    console.log('[PermissionIndicator] Adding permission to role:', selectedRoleToAdd.value)
    
    const roleId = selectedRoleToAdd.value
    const targetPerm = selectedPermission.value

    await axios.post(`/admin/roles/${roleId}/assign-permission/`, {
      permission_id: targetPerm.id
    })

    const addedRole = allRoles.value.find(r => r.id === roleId)
    successMessage.value = `Permission "${targetPerm.code}" added to "${addedRole?.name}" successfully!`

    // Access inheritance: adding a role to a SUB permission (e.g. view_tasks)
    // must also grant the parent/main permission (e.g. view) — otherwise the
    // role can't even open the screen. The reverse does NOT cascade: adding to
    // the main permission never touches sub-permissions.
    const parent = parentPermission.value
    const isSub = parent && !targetPerm.is_requested && targetPerm.id !== parent.id
    if (isSub) {
      const parentAlreadyHas = (parent.roles || []).some(r => r.id === roleId)
      if (!parentAlreadyHas) {
        try {
          await axios.post(`/admin/roles/${roleId}/assign-permission/`, {
            permission_id: parent.id
          })
          successMessage.value = `Permission "${targetPerm.code}" added to "${addedRole?.name}", and access to "${parent.code}" was granted automatically.`
        } catch (inheritErr) {
          console.error('[PermissionIndicator] Failed to auto-grant parent permission:', inheritErr)
        }
      }
    }
    
    // Reset form
    showAddRole.value = false
    selectedRoleToAdd.value = null
    selectedPermission.value = null
    
    // Reload data
    await loadRoles()
    
  } catch (err) {
    console.error('[PermissionIndicator] Error adding permission:', err)
    error.value = err.response?.data?.error || 'Failed to add permission to role'
  } finally {
    saving.value = false
  }
}

// Remove permission from role
const removePermissionFromRole = async (perm, role) => {
  removingKey.value = `${perm.id}-${role.id}`
  error.value = null
  successMessage.value = ''
  
  try {
    console.log('[PermissionIndicator] Removing permission from role:', perm.id, role.id)
    
    await axios.delete(`/admin/roles/${role.id}/remove-permission/${perm.id}/`)
    
    successMessage.value = `Permission "${perm.code}" removed from "${role.name}" successfully!`
    
    // Reload data
    await loadRoles()
    
  } catch (err) {
    console.error('[PermissionIndicator] Error removing permission:', err)
    error.value = err.response?.data?.error || 'Failed to remove permission from role'
  } finally {
    removingKey.value = null
  }
}

// Refresh current user's permissions
const refreshMyPermissions = async () => {
  refreshingMyPermissions.value = true
  myPermissionsRefreshed.value = false
  
  try {
    await authStore.refreshPermissions()
    myPermissionsRefreshed.value = true
    
    // Reload page after short delay to apply new menu filtering
    setTimeout(() => {
      window.location.reload()
    }, 1500)
    
  } catch (err) {
    console.error('[PermissionIndicator] Error refreshing permissions:', err)
    error.value = 'Failed to refresh permissions. Please re-login.'
  } finally {
    refreshingMyPermissions.value = false
  }
}

// Reset when dialog closes
watch(dialog, (newVal) => {
  permissionDialogOpen.value = newVal
  console.log('[PermissionIndicator] Dialog state changed:', newVal)
  if (!newVal) {
    // Small delay before clearing to avoid flicker
    setTimeout(() => {
      if (!dialog.value) {
        relatedPermissions.value = []
        allRoles.value = []
        modulePrefix.value = ''
        error.value = null
        successMessage.value = ''
        showAddRole.value = false
        selectedRoleToAdd.value = null
        selectedPermission.value = null
        expandedPanels.value = []
        showSubPermissions.value = false
        myPermissionsRefreshed.value = false
      }
    }, 300)
  }
})

// If the indicator is unmounted while its dialog is open (e.g. flyout closed by
// some other code path), release the shared flag so the menu doesn't get stuck.
onUnmounted(() => {
  if (dialog.value) {
    permissionDialogOpen.value = false
  }
})
</script>

<style scoped>
.permission-indicator-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  cursor: pointer;
  position: relative;
  z-index: 10;
}

.permission-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.05);
  opacity: 0.7;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
}

.permission-indicator:hover {
  opacity: 1;
  transform: scale(2.5);
  background: rgba(25, 118, 210, 0.15);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.permission-indicator:active {
  transform: scale(2.2);
}

.roles-list {
  max-height: 200px;
  overflow-y: auto;
  background: transparent;
}

.roles-list .v-list-item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.gap-2 {
  gap: 8px;
}

.permission-panel.highlighted {
  border-left: 3px solid #fb8c00;
}

.permission-panel .v-expansion-panel-title {
  min-height: 44px;
}

.border {
  border: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
